import {Link} from '@wandb/weave/common/util/links';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {TooltipDeprecated} from '@wandb/weave/components/TooltipDeprecated';
import React, {FC, useCallback, useMemo, useState} from 'react';
import {SortFunction} from 'react-table';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Loader} from 'semantic-ui-react';

import {Invite} from '../../../generated/graphql';
import {
  MemberData,
  TeamBaseEntity,
  TeamOrganization,
} from '../../../graphql/teamQuery';
import {Viewer} from '../../../state/viewer/types';
import {checkIsServiceAccount} from '../../../util/serviceAccounts';
import TypedWBReactTable, {
  DataColumn,
  DataColumns,
  DataRow,
} from '../../WBReactTable/TypedWBReactTable';
import {ModelSeatTypeDropdown} from '../ModelSeatTypeDropdown';
import {RoleOption} from '../TeamOrOrganizationRoleOption';
import {
  getModelsSeatTypeDisplayName,
  getWeaveRoleTypeDisplayName,
  ModelSeatTypeColumnName,
  ModelsSeatType,
  WeaveRoleType,
} from '../util';
import {WeaveRoleTypePill} from '../WeaveRoleTypePill';
import {TeamMemberActionMenu} from './TeamMemberActionMenu';
import {TeamMembersRole} from './TeamMembersRole';
import * as S from './TeamMembersTable.styles';
import {getRoleLabel, InviteKey, TeamMemberRole} from './utils';

interface TeamMembersTableProps {
  roleOptions: RoleOption[];
  isViewerAdmin: boolean;
  entity: TeamBaseEntity;
  organization?: TeamOrganization;
  viewer?: Viewer;
  refetch?: () => void;
}

type Row = {
  name: MemberData;
  email: MemberData;
  role: MemberData;
  action: [MemberData, InviteKey];
  model_seat: [MemberData, ModelsSeatType];
  weave_seat: [MemberData, WeaveRoleType];
};

function getSearchOptions(row: Row): string[] {
  const memberData = row.name;
  return [
    memberData.name,
    memberData.username,
    memberData.email,
    getRoleLabel(memberData),
  ];
}
export const TeamMembersTable: FC<TeamMembersTableProps> = ({
  roleOptions,
  isViewerAdmin,
  entity,
  organization,
  refetch,
  viewer,
}) => {
  const getIsMemberCurrentViewer = useCallback(
    (member: MemberData) => {
      return viewer?.username === member.username;
    },
    [viewer]
  );

  const [isSettingRole, setIsSettingRole] = useState(false);

  const isViewerOrgAdmin =
    organization?.members.find(
      mem => mem.username != null && mem.username === viewer?.username
    )?.role === TeamMemberRole.ADMIN;

  function parseInvites(node: Invite) {
    const user = node.toUser;
    const result: InviteKey = {
      id: node.id,
      name: node?.name ?? '',
      createdAt: node?.createdAt ?? new Date(0).toISOString(),
      email: node?.email ?? '',
      username: user?.username ?? '',
      wasCreatedByViewer:
        node?.fromUser?.username === viewer?.username &&
        viewer?.username != null,
    };
    return result;
  }

  const invites: InviteKey[] = entity?.invites?.edges
    ?.map(x => x?.node)
    ?.map(parseInvites);
  // Create map of user email to invite metadata using list of invites
  const latestUniqueInvites = useMemo<Record<string, InviteKey>>(() => {
    return invites?.reduce(
      (set, invite) => {
        if (
          !set[invite.email] ||
          set[invite.email]?.createdAt < invite.createdAt
        ) {
          set[invite.email] = invite;
        }
        return set;
      },
      {} as {[key: string]: InviteKey}
    );
  }, [invites]);

  // Check the sort function if you add/remove columns
  const columns: DataColumns<Row> = useMemo(() => {
    const nameColumn: DataColumn<Row> = {
      Header: 'PROFILE',
      accessor: 'name',
      minWidth: 100,
      Cell: cellInfo => {
        const {name, username, photoUrl, pending} = cellInfo.value;
        if (pending) {
          return (
            <div className="flex items-center">
              <img
                src="/default-profile-picture.png"
                alt={`${name}'s profile picture`}
                className="h-36 w-36 rounded-full"
              />
              <div className="overflow-visible pl-5">
                <div className="inline-flex items-center">
                  <i>Invited User</i>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="flex items-center">
            <img
              src={photoUrl ?? '/default-profile-picture.png'}
              className="h-36 w-36 rounded-full"
              alt={`${name}'s profile picture`}
            />
            <div className="overflow-visible pl-5">
              <div className="inline-flex items-center">{name}</div>
              <div>
                <Link className="text-teal-550" to={`/${username}`}>
                  @{username}
                </Link>
              </div>
            </div>
          </div>
        );
      },
    };
    const emailColumn: DataColumn<Row> = {
      Header: 'EMAIL',
      accessor: 'email',
      minWidth: 50,
      Cell: cellInfo => {
        const {email} = cellInfo.value;
        return <div className="whitespace-normal break-words">{email}</div>;
      },
    };
    const baseColumns: DataColumns<Row> = [nameColumn, emailColumn];

    const roleColumn: DataColumn<Row> = {
      Header: () => <div className="pl-5">TEAM ROLE</div>,
      accessor: 'role',
      minWidth: 80,
      style: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'left',
      },
      Cell: cellInfo => {
        const {username} = cellInfo.value;
        // org admins can edit their own roles in teams
        const canViewerEditSelfRole =
          isViewerOrgAdmin && username === viewer?.username;
        return (
          <TeamMembersRole
            roleOptions={roleOptions}
            canViewerPerformActions={isViewerAdmin || canViewerEditSelfRole}
            entity={entity}
            member={cellInfo.value}
            refetch={refetch}
            setIsLoading={setIsSettingRole}
          />
        );
      },
    };

    const modelSeatColumn: DataColumn<Row> = {
      Header: () => (
        <TooltipDeprecated
          position="top center"
          className="flex items-center"
          trigger={
            <div className="flex items-center pl-5">
              MODELS SEAT
              <Icon name="info" className="ml-5" />
            </div>
          }>
          <Tailwind>
            <div className="flex items-center">
              A models seat gives you write access to model projects so that you
              can contribute and manage a models project. Organization admins
              can control access on the organization members page.
            </div>
          </Tailwind>
        </TooltipDeprecated>
      ),
      accessor: 'model_seat',
      minWidth: 60,
      sortable: false,
      Cell: cellInfo => {
        const [memberValue, modelsSeat] = cellInfo.value;
        const {pending} = memberValue;
        if (pending) {
          return <div className="flex pl-5 text-moon-450">Invited</div>;
        }
        return (
          <ModelSeatTypeDropdown
            currentModelSeat={modelsSeat}
            username={memberValue.username}
            isDisabled={true}
          />
        );
      },
    };

    const weaveRoleColumn: DataColumn<Row> = {
      Header: 'WEAVE ACCESS',
      accessor: 'weave_seat',
      minWidth: 60,
      sortable: false,
      Cell: cellInfo => {
        const [memberValue, weaveRole] = cellInfo.value;
        const {pending} = memberValue;
        if (pending) {
          return <div className="flex pl-5 text-moon-450">Invited</div>;
        }
        return <WeaveRoleTypePill currentWeaveRole={weaveRole} />;
      },
    };

    baseColumns.push(roleColumn);
    baseColumns.push(modelSeatColumn);
    baseColumns.push(weaveRoleColumn);

    const actionColumn: DataColumn<Row> = {
      Header: '',
      accessor: 'action',
      maxWidth: 80,
      style: {
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
      },
      Cell: cellInfo => {
        const [member, inviteKey] = cellInfo.value;
        return (
          <TeamMemberActionMenu
            member={member}
            organization={organization}
            entity={entity}
            refetch={refetch}
            isViewerAdmin={isViewerAdmin}
            getIsMemberCurrentViewer={getIsMemberCurrentViewer(member)}
            inviteKey={inviteKey}
          />
        );
      },
    };
    return [...baseColumns, actionColumn];
  }, [
    isViewerAdmin,
    isViewerOrgAdmin,
    entity,
    refetch,
    getIsMemberCurrentViewer,
    organization,
    viewer?.username,
    roleOptions,
  ]);

  const rows: DataRow<Row>[] = useMemo(
    () =>
      entity.members
        .filter(m => !checkIsServiceAccount(m.accountType))
        .map((member: MemberData) => {
          const {name, username, email, role} = member;
          const nameColText = name ?? '';
          const usernameColText = username ?? '';
          const label = getRoleLabel(member);
          const inviteKey = latestUniqueInvites?.[email];
          const orgMembersModelSeat =
            organization?.members.find(
              mem => mem.username != null && mem.username === username
            )?.modelsSeat ?? 'full';
          let modelsSeat = getModelsSeatTypeDisplayName(
            orgMembersModelSeat as ModelSeatTypeColumnName
          );
          const orgMembersWeaveRole =
            organization?.members.find(
              mem => mem.username != null && mem.username === username
            )?.weaveRole ?? 'full';
          let weaveRole = getWeaveRoleTypeDisplayName(orgMembersWeaveRole);
          if (role === TeamMemberRole.VIEW_ONLY) {
            // Although the user may have Org Level Full Models Access they will still only be view-only if team role is view only
            if (modelsSeat !== ModelsSeatType.NOACCESS) {
              modelsSeat = ModelsSeatType.VIEWER;
            }
            if (weaveRole !== WeaveRoleType.NOACCESS) {
              weaveRole = WeaveRoleType.VIEWER;
            }
          }

          return {
            row: {
              name: member,
              email: member,
              role: member,
              action: [member, inviteKey],
              model_seat: [member, modelsSeat],
              weave_seat: [member, weaveRole],
            },
          };
        }),
    [entity.members, latestUniqueInvites, organization?.members]
  );

  // Edit this sort function if you add/remove columns
  const defaultSortMethod: SortFunction = useCallback((a, b, desc) => {
    let aVal = a;
    let bVal = b;

    // get proper value for 'name' column
    if (a && a.isServiceAccount != null) {
      aVal = a.text;
      bVal = b.text;
    }

    // get proper values for 'role' column
    if (a && a.role != null) {
      aVal = getRoleLabel(a);
      bVal = getRoleLabel(b);
    }

    aVal = aVal?.toLowerCase().trim();
    bVal = bVal?.toLowerCase().trim();

    // Empty cells are always at bottom of table when sorted
    if (desc) {
      if (aVal === '' && bVal !== '') {
        return -1;
      }
      if (aVal !== '' && bVal === '') {
        return 1;
      }
      if (aVal === '' && aVal === bVal) {
        return 0;
      }
    }

    // perform sort
    if (aVal > bVal) {
      return -1;
    }
    if (bVal > aVal) {
      return 1;
    }
    return 0;
  }, []);

  if (!entity) {
    return (
      <S.LoaderWrapper>
        <Loader name="team-members-table-loader" />
      </S.LoaderWrapper>
    );
  }

  const teamMembersNoDataComponent = () => {
    return (
      <Tailwind>
        <div className="text-gray-600 flex h-40 w-full items-center justify-center">
          <p className="text-lg font-semibold">No rows found</p>
        </div>
      </Tailwind>
    );
  };

  return (
    <Tailwind>
      <TypedWBReactTable<Row>
        dataTest="team-members-table"
        className="mt-8 overflow-visible border-moon-250"
        data={rows}
        columns={columns}
        searchPlaceholder={'Search by name, roles, teams...'}
        defaultSortMethod={defaultSortMethod}
        defaultSorted={[{id: 'username', desc: true}]}
        pageSize={20}
        getTrProps={() => ({
          className: 'flex items-center min-h-64',
        })}
        noDataComponent={teamMembersNoDataComponent}
        loading={isSettingRole}
        getSearchOptions={getSearchOptions}
      />
    </Tailwind>
  );
};
